import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { ProgressSnackComponent } from '../_offline/component/dialog/progress-snack/progress-snack.component';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ProgressSnackBarService {

  public snackBarRef: MatSnackBarRef<any>;
  private startTime: number;
  private totalTime: number;
  private progress: number = 0;
  private isPageActive: boolean = true;
  private isToastActive: boolean = false;
  private forceComplete: boolean = false;

  constructor(
    public snackBar: MatSnackBar,
    private translate: TranslateService,
    private router: Router
  ) {
    this.router.events.subscribe(event => {
      this.setPageActiveState(false);
    });
  }

  private resetState(): void {
    this.closeSnackBar();
    this.startTime = 0;
    this.totalTime = 0;
    this.progress = 0;
    this.isToastActive = false;
    this.forceComplete = false;
  }

  openCustomSnackBar(message: string, panelClass: string = null, duration: number = 0): void {
    const config = new MatSnackBarConfig();
    config.panelClass = panelClass ? [panelClass] : [];
    config.duration = duration;
    config.data = { message };
    this.snackBarRef = this.snackBar.openFromComponent(ProgressSnackComponent, config);
    this.forceComplete = false;
  }

  updateProgress(message: string, progress: number, isCompleted: boolean = false, reportId: string = ""): void {
    if (this.snackBarRef && this.snackBarRef.instance instanceof ProgressSnackComponent) {
      this.snackBarRef.instance.message = message;
      this.snackBarRef.instance.progress = progress;
      this.snackBarRef.instance.isCompleted = isCompleted;
      this.snackBarRef.instance.reportId = reportId;
      this.snackBarRef.instance.isPageActive = this.isPageActive;
      this.snackBarRef.instance.closePressed.subscribe(() => {
        this.snackBarRef.dismiss();
      });
    }
  }

  showProgress(title: string, version: number, imageCount: number, secondsPerImage: number, reportId: string): void {
    const createReportToastProgressMessage = this.translate.instant('inspectionReport.CreateReportToastInitial', { title, version });
    const createReportToastCompletedMessage = this.translate.instant('inspectionReport.CreateReportToastCompleted', { title, version });

    // If toast is already active and PDF creation still pending. Update current progress in view
    if (this.isToastActive && this.progress < 100) {
      const elapsedTime = Date.now() - this.startTime;
      this.progress = Math.min((elapsedTime / this.totalTime) * 100, 99);
      this.updateProgress(createReportToastProgressMessage, this.progress);
    } else {
      // Initialize progress toast and display in view
      this.resetState();

      this.totalTime = Math.max(1, imageCount * secondsPerImage * 1000);
      this.startTime = Date.now();
      this.isToastActive = true;

      this.openCustomSnackBar(createReportToastProgressMessage, 'snack-progress-custom');
      this.updateProgress(createReportToastProgressMessage, 0);

      const interval = setInterval(() => {
        const elapsedTime = Date.now() - this.startTime;
        this.progress = Math.min((elapsedTime / this.totalTime) * 100, 99);

        this.updateProgress(createReportToastProgressMessage, this.progress);

        if ((this.progress >= 99 && !this.isPageActive) || this.forceComplete) {

          this.updateProgress(createReportToastCompletedMessage, 100, true, reportId);

          setTimeout(() => {
            this.closeSnackBar();
            this.isToastActive = false;
          }, 5000);

          clearInterval(interval);
        }
      }, 500);
    }
  }

  closeSnackBar(): void {
    if (this.snackBarRef) {
      this.snackBarRef.dismiss();
    }
    this.isToastActive = false;
    this.forceComplete = false;
  }

  setPageActiveState(isActive: boolean): void {
    this.isPageActive = isActive;
  }

  getRemainingTime(): number {
    const elapsedTime = Date.now() - this.startTime;
    return Math.max(this.totalTime - elapsedTime, 0);
  }

  public forceCompleteStatus(): void {
    if (this.snackBarRef && this.snackBarRef.instance instanceof ProgressSnackComponent) {
      this.forceComplete = true;
    }
  }
}
